'use client';

import { useCallback, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useProjectDetailsFragment } from '../../project/gql/generated/ProjectDetails';
import {
  ProjectSettingsModal,
  type ProjectSettingsModalProps,
} from '../components/ProjectSettingsModal';

export type ShowProjectSettingsModalInput = {
  onRequestClose?: () => void;
  projectId: string;
};

export type UseProjectSettingsModalInput = {
  onRequestClose?: () => void;
};

export type UseProjectSettingsModalReturn = {
  projectSettingsModal: React.ReactNode;
  showProjectSettingsModal: (input: ShowProjectSettingsModalInput) => void;
  hideProjectSettingsModal: () => void;
};

export const useProjectSettingsModal = (): UseProjectSettingsModalReturn => {
  const accountId = useActiveAccountId();
  const [props, setProps] = useState<ProjectSettingsModalProps>({
    accountId,
    onRequestClose: noop,
    isShowing: false,
    workspaceId: '',
    projectId: '',
    trackingSource: '',
  });

  const { data } = useProjectDetailsFragment(props.projectId);
  const workspaceId = useMemo(() => data?.workspace?.id ?? '', [data]);

  const projectSettingsModal = useMemo(
    () => <ProjectSettingsModal {...props} workspaceId={workspaceId} />,
    [props, workspaceId]
  );

  const hideProjectSettingsModal = useCallback(() => {
    setProps((prev) => ({
      ...prev,
      isShowing: false,
      onRequestClose: noop,
      workspaceId: '',
      projectId: '',
    }));
  }, []);

  const showProjectSettingsModal = useCallback(
    ({
      onRequestClose = hideProjectSettingsModal,
      projectId,
    }: ShowProjectSettingsModalInput) => {
      setProps((prev) => ({
        ...prev,
        isShowing: true,
        onRequestClose,
        projectId,
      }));
    },
    [hideProjectSettingsModal]
  );

  return {
    projectSettingsModal,
    showProjectSettingsModal,
    hideProjectSettingsModal,
  };
};
